/**
 * RefUploadPage — public no-auth uploader for ref-pack share links.
 *
 * Route: /ref-upload/:token  (registered OUTSIDE ProtectedRoute in App.js).
 *
 * The page is a stripped-down dropzone — no nav, no chrome — that the
 * cinematographer / stylist / location scout can open on their phone and
 * use to drop reference photos directly onto an Emergent Crew Pre-Production
 * card without needing an Emergent account.
 *
 * Backend contract:
 *   GET  /api/public/crew-ref-share/{token}        → card info + capacity
 *   POST /api/public/crew-ref-share/{token}/upload → multipart `files`
 *
 * The GET response NEVER leaks brief text or existing references; only
 * project_name, candidate_name, kind, remaining_capacity, expires_at.
 */
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Upload, Check, AlertCircle, X, Image as ImageIcon } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const KIND_LABEL = {
  cast: "Cast",
  costume: "Costume",
  corner: "Location",
};

export default function RefUploadPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [staged, setStaged] = useState([]);            // [{file, preview, status, error?}]
  const [dragOver, setDragOver] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/public/crew-ref-share/${token}`);
        if (alive) {
          setInfo(data);
          setLoading(false);
        }
      } catch (e) {
        if (!alive) return;
        const detail = e.response?.data?.detail || e.message;
        setError({ status: e.response?.status || 0, detail });
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const accept = "image/jpeg,image/jpg,image/png,image/webp";
  const remaining = Math.max(0, (info?.remaining_capacity ?? 0) - staged.length);

  const addFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const incoming = Array.from(fileList).filter(f => f && f.type.startsWith("image/"));
    if (incoming.length === 0) {
      setError({ status: 0, detail: "Drop image files only (JPEG / PNG / WebP)" });
      return;
    }
    const slots = info?.remaining_capacity ?? 0;
    const free = Math.max(0, slots - staged.length);
    const accepted = incoming.slice(0, free);
    if (incoming.length > free) {
      setError({ status: 0, detail: `Card has ${slots} slot(s) free — ${incoming.length - free} file(s) skipped.` });
    } else {
      setError(null);
    }
    const previews = accepted.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      status: "staged",
    }));
    setStaged(prev => [...prev, ...previews]);
  };

  const removeStaged = (idx) => {
    setStaged(prev => {
      const next = [...prev];
      const removed = next.splice(idx, 1)[0];
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return next;
    });
  };

  const handleUpload = async () => {
    if (staged.length === 0 || uploading) return;
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      staged.forEach(s => fd.append("files", s.file));
      const { data } = await axios.post(
        `${API}/api/public/crew-ref-share/${token}/upload`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      // Mark all staged as done; revoke previews to free memory.
      staged.forEach(s => s.preview && URL.revokeObjectURL(s.preview));
      setUploadedCount(c => c + (data.added || 0));
      setStaged([]);
      // Refresh remote capacity.
      setInfo(i => i ? { ...i, remaining_capacity: data.remaining_capacity } : i);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setError({ status: e.response?.status || 0, detail });
    }
    setUploading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
      </div>
    );
  }

  if (error && !info) {
    const isExpired = error.status === 410;
    const isMissing = error.status === 404;
    return (
      <div className="min-h-screen bg-[#0a0e1a] text-white flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center space-y-4">
          <div className="inline-flex p-3 rounded-full bg-rose-500/10 text-rose-300">
            <AlertCircle className="w-10 h-10" />
          </div>
          <h1 className="text-xl font-semibold">
            {isExpired ? "Link expired" : isMissing ? "Link not found" : "Couldn't load link"}
          </h1>
          <p className="text-sm text-white/60">
            {isExpired
              ? "This share link is no longer active. Ask the project owner for a fresh one."
              : isMissing
              ? "We couldn't find this share link. It may have been revoked or never existed."
              : error.detail}
          </p>
        </div>
      </div>
    );
  }

  const fullySent = uploadedCount > 0 && staged.length === 0 && info.remaining_capacity === 0;

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-white" data-testid="ref-upload-page">
      <div className="max-w-2xl mx-auto px-4 py-10 sm:py-16">
        <div className="mb-8 text-center sm:text-left">
          <div className="text-xs text-cyan-400 font-mono tracking-wider uppercase mb-1">
            EiPi Media · Reference upload
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-white" data-testid="ref-upload-card-title">
            {info.candidate_name}
          </h1>
          <div className="text-sm text-white/50 mt-1">
            <span className="text-white/70">{info.project_name}</span>
            <span className="mx-2 text-white/30">·</span>
            <span>{KIND_LABEL[info.kind] || info.kind}</span>
            <span className="mx-2 text-white/30">·</span>
            <span className={info.remaining_capacity === 0 ? "text-rose-300" : "text-emerald-300"}>
              {info.remaining_capacity} of 6 slots left
            </span>
          </div>
        </div>

        {fullySent && (
          <div className="mb-6 rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-4 flex items-start gap-3"
               data-testid="ref-upload-done-banner">
            <Check className="w-5 h-5 text-emerald-300 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <div className="font-medium text-emerald-200">All slots filled — thank you!</div>
              <div className="text-white/60 mt-1">
                {uploadedCount} image{uploadedCount === 1 ? "" : "s"} delivered.
                You can close this page.
              </div>
            </div>
          </div>
        )}

        {info.remaining_capacity > 0 && (
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => {
              e.preventDefault();
              setDragOver(false);
              addFiles(e.dataTransfer?.files);
            }}
            onClick={() => fileInputRef.current?.click()}
            className={`rounded-2xl border-2 border-dashed transition-colors cursor-pointer p-8 sm:p-12 text-center
              ${dragOver
                ? "border-cyan-400 bg-cyan-500/10"
                : "border-white/15 hover:border-cyan-400/60 hover:bg-cyan-500/5"}`}
            data-testid="ref-upload-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={accept}
              multiple
              className="hidden"
              onChange={e => { addFiles(e.target.files); e.target.value = ""; }}
              data-testid="ref-upload-file-input"
            />
            <div className="inline-flex p-3 rounded-full bg-cyan-500/10 text-cyan-300 mb-4">
              <Upload className="w-8 h-8" />
            </div>
            <div className="text-base text-white/90 font-medium">
              Drop photos here, or tap to choose
            </div>
            <div className="text-xs text-white/40 mt-2">
              JPEG · PNG · WebP &nbsp;·&nbsp; up to {info.max_file_mb} MB each
              &nbsp;·&nbsp; {remaining} slot{remaining === 1 ? "" : "s"} left
            </div>
          </div>
        )}

        {staged.length > 0 && (
          <div className="mt-6">
            <div className="text-xs text-white/50 mb-2 uppercase tracking-wider">
              Ready to send ({staged.length})
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {staged.map((s, idx) => (
                <div key={idx} className="relative aspect-square rounded-lg overflow-hidden bg-black/40 border border-white/10 group">
                  <img src={s.preview} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => removeStaged(idx)}
                    className="absolute top-1 right-1 p-1 rounded-full bg-black/70 text-white/90 opacity-0 group-hover:opacity-100 hover:bg-rose-500"
                    title="Remove from upload"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="mt-4 w-full sm:w-auto px-6 py-3 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="ref-upload-submit-btn"
            >
              {uploading
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
                : <><Upload className="w-4 h-4" /> Send {staged.length} photo{staged.length === 1 ? "" : "s"}</>}
            </button>
          </div>
        )}

        {error && info && (
          <div className="mt-4 rounded-lg bg-rose-500/10 border border-rose-500/30 p-3 flex items-start gap-2"
               data-testid="ref-upload-error-banner">
            <AlertCircle className="w-4 h-4 text-rose-300 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-rose-200">{error.detail}</div>
          </div>
        )}

        {uploadedCount > 0 && !fullySent && (
          <div className="mt-4 text-xs text-emerald-300/80 flex items-center gap-2">
            <Check className="w-3 h-3" /> {uploadedCount} image{uploadedCount === 1 ? "" : "s"} delivered so far
          </div>
        )}

        <div className="mt-12 pt-6 border-t border-white/5 text-[10px] text-white/30 text-center">
          Powered by EiPi Media · This link expires automatically.
        </div>
      </div>
    </div>
  );
}
